import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { customersAPI, coreAPI, performanceSettingsAPI, CustomerProfile, CustomerDashboardStats, Site } from '../../lib/api';
import api from '../../lib/api';
import { getErrorMessage } from '../../hooks/useFetchData';
import { useView } from '../../contexts/ViewContext';
import { CustomerWithStatus, MapLocation } from './types';
import { mapStatusToType } from './utils';
import type { CustomerFilters } from './SearchFilterBar';
import { EMPTY_FILTERS } from './SearchFilterBar';
import {
  classifyOtdBand,
  parseOtdThresholds,
  type OtdThresholds,
  DEFAULT_OTD_THRESHOLDS,
} from './otdFilters';

export function useCustomers() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resolveTabFromUrl = useCallback((): 'all' | 'sites' | 'groups' | 'intra' => {
    const tab = searchParams.get('tab');
    if (tab === 'groups') return 'groups';
    if (tab === 'intra') return 'intra';
    if (tab === 'all') return 'all';
    return 'sites';
  }, [searchParams]);

  const { viewKey, getViewQueryParams, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId, isLoading: viewContextLoading } = useView();
  const [customers, setCustomers] = useState<CustomerWithStatus[]>([]);
  const [stats, setStats] = useState<CustomerDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  // CR-3953: Filter state for customer list
  const [filters, setFilters] = useState<CustomerFilters>(EMPTY_FILTERS);
  const [siteOptions, setSiteOptions] = useState<{ value: string; label: string }[]>([]);
  const [otdThresholds, setOtdThresholds] = useState<OtdThresholds>(DEFAULT_OTD_THRESHOLDS);

  // Current tab (all, sites, groups)
  const [currentTab, setCurrentTab] = useState<'all' | 'sites' | 'groups' | 'intra'>(resolveTabFromUrl);

  useEffect(() => {
    setCurrentTab(resolveTabFromUrl());
  }, [resolveTabFromUrl]);
  // CR-4578: When user clicks a Customer Group card, filter sites tab to that group
  const [selectedGroupName, setSelectedGroupName] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Search
  const searchRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Customer locations for map
  const [mapLocations, setMapLocations] = useState<MapLocation[]>([]);

  const fetchData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);

      const viewParams = getViewQueryParams();
      const [profilesResponse, statsResponse, sitesResponse, contractsResponse, performanceResponse, companyGroupsResponse] = await Promise.all([
        customersAPI.getProfiles({ page_size: 1000, minimal: true, is_approved: true, ...viewParams }),
        customersAPI.getDashboardStats(viewParams),
        coreAPI.getAccessibleSites(),
        api.get('/purchasing/contract-prices/', { params: { page_size: 1000 } }).catch(() => ({ data: { results: [] } })),
        performanceSettingsAPI.getSettings().catch(() => null),
        coreAPI.getCustomerGroupsForFilter().catch(() => ({ data: { results: [] } })),
      ]);

      // CR-3953: Build site filter options
      const sites = (sitesResponse.data as unknown as Site[]) || [];
      setSiteOptions(
        sites.map((s) => ({ value: String(s.id), label: s.name }))
      );

      setOtdThresholds(parseOtdThresholds(performanceResponse?.data ?? null));

      const customersData = profilesResponse.data.results || [];

      // Build set of customer names with active contracts
      const activeContracts = contractsResponse.data.results || contractsResponse.data || [];
      const contractCustomerNames = new Set(
        (activeContracts as Array<Record<string, unknown>>)
          .filter((c) => c['status'] === 'active')
          .map((c) => c['customer_name'] as string)
          .filter(Boolean)
      );

      // Map customers with real onboarding_status from backend
      const customersWithStatus: CustomerWithStatus[] = customersData.map((s: CustomerProfile) => ({
        ...s,
        onboarding_status_derived: mapStatusToType(s.onboarding_status, s.is_approved),
        has_contract: contractCustomerNames.has(s.company_name),
        draft_progress: (s as unknown as Record<string, unknown>)['draft_progress'] as number ?? 0,
        is_customer_group: false,
        // CR-3825: Use real API value for questionnaire responses
        has_questionnaire_responses: !!s.has_questionnaire_responses,
      }));

      // CR-4556: Build customer group entries for the "Customer Groups" tab.
      // Use the dedicated /core/company-groups/ endpoint so all groups appear
      // even when there are >100 customer companies (DRF caps at max_page_size=100).
      const companyGroups = (companyGroupsResponse.data as { results?: Array<Record<string, unknown>> }).results || [];
      const groupEntries: CustomerWithStatus[] = companyGroups.map((g, idx) => ({
        id: -(idx + 1),  // Negative IDs to avoid collision
        company: -(idx + 1),
        company_name: g['name'] as string,
        onboarding_status: 'approved',
        is_approved: true,
        is_active: true,
        has_questionnaire_responses: false,
        onboarding_status_derived: 'approved' as const,
        has_contract: false,
        draft_progress: 0,
        is_customer_group: true,
        customer_count: (g['company_count'] as number) ?? 0,
        group_id: g['id'] as number,
      } as unknown as CustomerWithStatus));

      customersWithStatus.push(...groupEntries);

      setCustomers(customersWithStatus);
      setStats(statsResponse.data);

      // Extract locations for map — use real coordinates, city lookup, or country fallback
      // CR-4563 rework: City-level lookup for accurate pin placement
      const cityCoords: Record<string, [number, number]> = {
        'london': [51.51, -0.13], 'birmingham': [52.48, -1.90], 'manchester': [53.48, -2.24],
        'leeds': [53.80, -1.55], 'sheffield': [53.38, -1.47], 'liverpool': [53.41, -2.98],
        'bristol': [51.45, -2.59], 'newcastle': [54.98, -1.61], 'nottingham': [52.95, -1.15],
        'coventry': [52.41, -1.51], 'leicester': [52.63, -1.13], 'derby': [52.92, -1.47],
        'warrington': [53.39, -2.59], 'wolverhampton': [52.59, -2.13], 'stoke': [53.00, -2.18],
        'reading': [51.45, -0.97], 'southampton': [50.91, -1.40], 'portsmouth': [50.80, -1.09],
        'plymouth': [50.37, -4.14], 'exeter': [50.72, -3.53], 'swindon': [51.56, -1.78],
        'gloucester': [51.86, -2.24], 'oxford': [51.75, -1.26], 'cambridge': [52.21, 0.12],
        'norwich': [52.63, 1.30], 'ipswich': [52.06, 1.16], 'peterborough': [52.57, -0.24],
        'milton keynes': [52.04, -0.76], 'northampton': [52.24, -0.90], 'luton': [51.88, -0.42],
        'aberdeen': [57.15, -2.09], 'edinburgh': [55.95, -3.19], 'glasgow': [55.86, -4.25],
        'cardiff': [51.48, -3.18], 'swansea': [51.62, -3.94], 'belfast': [54.60, -5.93],
        'york': [53.96, -1.08], 'hull': [53.74, -0.33], 'chester': [53.19, -2.89],
        'bath': [51.38, -2.36], 'cheltenham': [51.90, -2.07], 'worcester': [52.19, -2.22],
        'dudley': [52.51, -2.08], 'walsall': [52.58, -1.98], 'bolton': [53.58, -2.43],
        'stockport': [53.41, -2.16], 'preston': [53.76, -2.70], 'bradford': [53.79, -1.75],
        'huddersfield': [53.65, -1.78], 'wakefield': [53.68, -1.50], 'doncaster': [53.52, -1.13],
        'rotherham': [53.43, -1.36], 'telford': [52.68, -2.45], 'crewe': [53.10, -2.44],
        'stafford': [52.81, -2.12], 'berlin': [52.52, 13.41], 'munich': [48.14, 11.58],
        'hamburg': [53.55, 9.99], 'frankfurt': [50.11, 8.68], 'paris': [48.86, 2.35],
        'lyon': [45.76, 4.84], 'amsterdam': [52.37, 4.90], 'dublin': [53.35, -6.26],
        'toronto': [43.65, -79.38], 'new york': [40.71, -74.01], 'chicago': [41.88, -87.63],
      };
      const countryCoords: Record<string, [number, number]> = {
        'United Kingdom': [52.5, -1.5], 'United States': [39.8, -98.5], 'Germany': [51.2, 10.4],
        'France': [46.6, 2.2], 'Poland': [52.0, 19.4], 'Italy': [42.5, 12.5], 'Spain': [40.4, -3.7],
        'Netherlands': [52.1, 5.3], 'Belgium': [50.8, 4.4], 'Sweden': [62.0, 15.0],
        'Norway': [60.5, 8.5], 'Denmark': [56.0, 10.0], 'Switzerland': [46.8, 8.2],
        'Austria': [47.5, 14.6], 'Czech Republic': [49.8, 15.5], 'Japan': [36.2, 138.3],
        'China': [35.9, 104.2], 'India': [20.6, 79.0], 'Canada': [56.1, -106.3],
        'Australia': [-25.3, 133.8], 'Ireland': [53.4, -8.2], 'Portugal': [39.4, -8.2],
        'Finland': [61.9, 25.7], 'Romania': [45.9, 24.97], 'Hungary': [47.2, 19.5],
        'Turkey': [38.9, 35.2], 'Mexico': [23.6, -102.6], 'Brazil': [-14.2, -51.9],
        'South Korea': [35.9, 127.8], 'Singapore': [1.35, 103.8], 'Malaysia': [4.2, 101.9],
        'Thailand': [15.9, 100.5], 'UK': [52.5, -1.5], 'USA': [39.8, -98.5],
      };
      const locations = customersWithStatus
        .map((s: CustomerWithStatus) => {
          const details = s.company_details as unknown as Record<string, unknown>;
          // CR-4563 rework: Use company_id as fallback, avoid undefined
          const customerId = s.company_id || s.company;
          if (!customerId) return null;
          const lat = details?.['latitude'] ? parseFloat(String(details['latitude'])) : null;
          const lng = details?.['longitude'] ? parseFloat(String(details['longitude'])) : null;
          if (lat && lng) return { id: customerId, name: s.company_name, lat, lng };
          // CR-4563 rework: Try city-level lookup for accurate placement
          const city = String(details?.['city'] || '').toLowerCase().trim();
          if (city && cityCoords[city]) {
            const [cLat, cLng] = cityCoords[city];
            const offset = ((customerId % 10) - 5) / 500;
            return { id: customerId, name: s.company_name, lat: cLat + offset, lng: cLng + offset };
          }
          // Fall back to country-level with offset
          const country = String(details?.['country'] || '');
          const coords = countryCoords[country];
          if (coords) {
            const offset = ((customerId % 20) - 10) / 20;
            const offset2 = (((customerId * 7) % 20) - 10) / 20;
            return { id: customerId, name: s.company_name, lat: coords[0] + offset, lng: coords[1] + offset2 };
          }
          return null;
        })
        .filter((loc): loc is MapLocation => loc !== null);
      setMapLocations(locations);

    } catch (err) {
      setError(getErrorMessage(err, 'Failed to load suppliers'));
    } finally {
      setLoading(false);
    }
  }, [viewKey, getViewQueryParams]);

  useEffect(() => {
    if (viewContextLoading) return;
    fetchData();
  }, [fetchData, viewContextLoading, viewLevel, selectedSiteId, selectedCompanyId, selectedGroupId]);

  // Reusable matcher so quick-filter chips can show counts without re-applying themselves.
  const matchesFilters = (
    s: CustomerWithStatus,
    opts: { skipRisk?: boolean; skipQuestionnaire?: boolean; skipOtd?: boolean; skipTab?: boolean } = {},
  ) => {
    if (searchQuery && !s.company_name.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (!opts.skipTab) {
      if (currentTab === 'groups' && !s.is_customer_group) return false;
      if (currentTab === 'sites' && s.is_customer_group) return false;
      if (currentTab === 'intra' && !s.is_intra) return false;
    }
    if (selectedGroupName && !s.is_customer_group) {
      const companyGroupName = (s.company_details as unknown as Record<string, unknown>)?.['group_name'] as string | undefined;
      if (companyGroupName !== selectedGroupName) return false;
    }
    const customerStatus = s.onboarding_status_derived || 'draft';
    if (customerStatus !== 'approved') return false;
    if (filters.site) {
      const supplierSites = s.suppliers?.toLowerCase() || '';
      const siteLabel = siteOptions.find((o) => o.value === filters.site)?.label?.toLowerCase() || '';
      if (!supplierSites.includes(siteLabel)) return false;
    }
    if (filters.status === 'active' && !s.company_details?.is_active) return false;
    if (filters.status === 'inactive' && s.company_details?.is_active) return false;
    if (filters.customer_code) {
      const code = s.company_details?.customer_code?.toLowerCase() || '';
      if (!code.includes(filters.customer_code.toLowerCase())) return false;
    }
    if (filters.strategic === 'yes' && !s.is_preferred) return false;
    if (filters.strategic === 'no' && s.is_preferred) return false;
    if (!opts.skipRisk && filters.risk && (s.risk_level || 'none') !== filters.risk) return false;
    if (!opts.skipQuestionnaire && filters.questionnaire) {
      const qOverall = s.questionnaire_status?.overall || 'none';
      if (qOverall !== filters.questionnaire) return false;
    }
    if (!opts.skipOtd && filters.otd) {
      if (s.is_customer_group) return false;
      const band = classifyOtdBand(s.on_time_delivery_rate, otdThresholds);
      if (band !== filters.otd) return false;
    }
    return true;
  };

  const filteredCustomers = customers.filter((s) => matchesFilters(s));

  // Counts for risk chips — apply all filters except the risk one itself.
  const riskCounts: Record<string, number> = { '': 0, low: 0, medium: 0, high: 0, critical: 0 };
  for (const s of customers) {
    if (matchesFilters(s, { skipRisk: true })) {
      riskCounts[''] += 1;
      const key = (s.risk_level || 'none');
      if (key in riskCounts) riskCounts[key] += 1;
    }
  }
  // Counts for questionnaire chips — apply all filters except the questionnaire one.
  const questionnaireCounts: Record<string, number> = { '': 0, up_to_date: 0, expiring: 0, overdue: 0, pending: 0, none: 0 };
  for (const s of customers) {
    if (matchesFilters(s, { skipQuestionnaire: true })) {
      questionnaireCounts[''] += 1;
      const key = s.questionnaire_status?.overall || 'none';
      if (key in questionnaireCounts) questionnaireCounts[key] += 1;
    }
  }

  // Counts for OTD chips — customer companies only; ignore tab (groups have no OTD).
  const otdCounts: Record<string, number> = { '': 0, excellent: 0, good: 0, fair: 0, poor: 0 };
  for (const s of customers) {
    if (s.is_customer_group) continue;
    if (matchesFilters(s, { skipOtd: true, skipTab: true })) {
      otdCounts[''] += 1;
      const band = classifyOtdBand(s.on_time_delivery_rate, otdThresholds);
      if (band in otdCounts) otdCounts[band] += 1;
    }
  }

  // OTD bands apply to customer companies, not group cards.
  useEffect(() => {
    if (currentTab === 'groups' && filters.otd) {
      setFilters((prev) => ({ ...prev, otd: '' }));
    }
  }, [currentTab, filters.otd]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, currentTab, filters]);

  // Paginated subset of filtered customers
  const totalPages = Math.ceil(filteredCustomers.length / pageSize);
  const paginatedCustomers = filteredCustomers.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSearch = () => {
    setSearchQuery(searchRef.current?.value || '');
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Customer code typeahead options derived from approved customers with a code set.
  const customerCodeOptions = customers
    .filter((s) => !s.is_customer_group)
    .map((s) => {
      const code = (s.company_details?.customer_code || '').trim();
      if (!code) return null;
      return { value: code, label: `${code} — ${s.company_name}` };
    })
    .filter((o): o is { value: string; label: string } => o !== null)
    .sort((a, b) => a.value.localeCompare(b.value));

  return {
    // Navigation
    navigate,

    // Loading states
    loading,
    error,

    // Data
    customers,
    filteredCustomers,
    stats,
    mapLocations,

    // Filters
    currentTab,
    setCurrentTab,
    selectedGroupName,
    setSelectedGroupName,
    showFilters,
    setShowFilters,
    filters,
    setFilters,
    riskCounts,
    questionnaireCounts,
    otdCounts,
    otdThresholds,
    siteOptions,
    customerCodeOptions,

    // Search
    searchRef,
    searchQuery,
    handleSearch,
    handleSearchKeyDown,
    handleClearSearch,

    // Pagination
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    paginatedCustomers,

    // Actions
    fetchData,
  };
}
